import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useNavigate } from "react-router-dom";
import { StatutBadge } from "@/components/StatutBadge";
import { formatDate } from "@/lib/statuts";
import { DatePicker } from "@/components/DatePicker";
import { Loader2, Plus, X, FolderKanban, CalendarClock, ClipboardList, Building2 } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [{ k: "today", l: "Jour" }, { k: "week", l: "Semaine" }, { k: "month", l: "Mois" }];

// ---------------- Dashboard ----------------
export function NovaSolDashboard() {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/novasol/dashboard?period=${period}`).then((r) => setD(r.data)).catch(() => {}); }, [period]);
  const K = d?.kpis;
  const cards = K ? [
    { l: "Dossiers", v: K.nb_dossiers, c: "#6366f1" },
    { l: "VT à planifier", v: K.vt_a_planifier, c: "#f59e0b" },
    { l: "VT planifiées", v: K.vt_planifiees, c: "#8b5cf6" },
    { l: "VT en cours", v: K.vt_en_cours, c: "#f97316" },
    { l: "VT à replanifier", v: K.vt_a_replanifier, c: "#ec4899" },
    { l: "VT terminées", v: K.vt_terminees, c: "#10b981" },
    { l: "Audits en cours", v: K.audits_en_cours, c: "#14b8a6" },
    { l: "Audits terminés", v: K.audits_termines, c: "#0ea5e9" },
    { l: "Travaux à lancer", v: K.travaux_a_lancer, c: "#ef4444" },
  ] : [];
  return (
    <div className="space-y-5" data-testid="novasol-dashboard">
      <Header title="NovaSol — Tableau de bord" subtitle="Suivi de l'activité NovaSol">
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          {PERIODS.map((p) => (
            <button key={p.k} data-testid={`nova-period-${p.k}`} onClick={() => setPeriod(p.k)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold ${period === p.k ? "bg-[#0A0A0A] text-white" : "text-slate-500 hover:text-slate-900"}`}>{p.l}</button>
          ))}
        </div>
      </Header>
      {!K ? <Spin /> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3">
          {cards.map((c) => (
            <div key={c.l} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.l}</div>
              <div className="mt-2 font-heading text-3xl font-black" style={{ color: c.c }}>{c.v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Rendez-vous NovaSol ----------------
export function NovaSolRendezVous() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/visites?origine=NOVASOL&limit=1000&sort_by=date_creation&sort_dir=-1").then((r) => setRows(r.data.items)).catch(() => setRows([])); }, []);
  return (
    <div className="space-y-4" data-testid="novasol-rdv">
      <Header title="Rendez-vous NovaSol" subtitle={`${rows?.length ?? 0} rendez-vous d'origine NovaSol`} />
      {!rows ? <Spin /> : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm" data-testid="novasol-rdv-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">N°</th><th className="px-4 py-2.5">Client</th><th className="px-4 py-2.5">Tél.</th><th className="px-4 py-2.5">CP / Ville</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Technicien</th><th className="px-4 py-2.5">Type VT</th><th className="px-4 py-2.5">Statut VT</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-slate-400">Aucun rendez-vous NovaSol</td></tr> : rows.map((r) => (
                  <tr key={r.id} data-testid={`novasol-rdv-row-${r.id}`} onClick={() => navigate(`/visites/${r.id}`)} className="cursor-pointer border-b border-slate-100 hover:bg-blue-50/40">
                    <td className="px-4 py-2.5 mono text-xs font-semibold text-amber-600">{r.numero_dossier}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-900">{r.client_nom}</td>
                    <td className="px-4 py-2.5 mono text-xs text-slate-600">{r.client_telephone}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{r.code_postal} {r.ville}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{r.date_visite ? formatDate(r.date_visite) : "—"} {r.heure_debut || ""}</td>
                    <td className="px-4 py-2.5 text-slate-700">{r.technicien_nom || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{r.type_visite}</td>
                    <td className="px-4 py-2.5"><StatutBadge code={r.statut} color={r.statut_color} label={r.statut_label} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Dossiers NovaSol ----------------
export function NovaSolDossiers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => { api.get("/novasol/dossiers").then((r) => setData(r.data)).catch(() => setData({ items: [], statuts_dossier: [] })); }, []);
  useEffect(() => { load(); }, [load]);

  const setStatut = async (did, statut) => {
    try { await api.patch(`/novasol/dossiers/${did}/statut`, { statut_dossier: statut }); toast.success("Statut dossier mis à jour"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="novasol-dossiers">
      <Header title="Dossiers NovaSol" subtitle={`${data?.items?.length ?? 0} dossier(s)`}>
        <button data-testid="nova-demander-vt" onClick={() => setShowForm(true)} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
          <Plus className="h-4 w-4" /> Demander une VT
        </button>
      </Header>
      {!data ? <Spin /> : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm" data-testid="novasol-dossiers-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Client</th><th className="px-4 py-2.5">Tél.</th><th className="px-4 py-2.5">CP / Ville</th><th className="px-4 py-2.5">Type VT</th><th className="px-4 py-2.5">Date VT</th><th className="px-4 py-2.5">Technicien</th><th className="px-4 py-2.5">Statut VT</th><th className="px-4 py-2.5">Docs</th><th className="px-4 py-2.5">Statut dossier</th>
              </tr></thead>
              <tbody>
                {data.items.length === 0 ? <tr><td colSpan={9} className="py-12 text-center text-slate-400">Aucun dossier. Cliquez sur « Demander une VT ».</td></tr> : data.items.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 font-medium text-slate-900 hover:underline cursor-pointer" onClick={() => d.visite_id && navigate(`/visites/${d.visite_id}`)}>{d.client_nom}</td>
                    <td className="px-4 py-2.5 mono text-xs text-slate-600">{d.client_telephone}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{d.code_postal} {d.ville}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{d.type_visite}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{d.date_visite ? formatDate(d.date_visite) : "—"}</td>
                    <td className="px-4 py-2.5 text-slate-700">{d.technicien_nom || "—"}</td>
                    <td className="px-4 py-2.5"><StatutBadge code={d.statut_visite} color={d.statut_visite_color} label={d.statut_visite_label} /></td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{d.nb_documents || 0}</td>
                    <td className="px-4 py-2.5">
                      <select data-testid={`dossier-statut-${d.id}`} value={d.statut_dossier} onChange={(e) => setStatut(d.id, e.target.value)} className="rounded-lg border border-slate-200 bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700">
                        {(data.statuts_dossier || []).map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showForm && <DemanderVTModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); toast.success("Demande de VT créée"); }} />}
    </div>
  );
}

function DemanderVTModal({ onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ prenom: "", nom: "", telephone: "", email: "", adresse: "", code_postal: "", ville: "", type_visite: "VT 174 Smart", date_visite: "", instructions: "" });
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/meta").then((r) => { setTypes(r.data.types_visite); setF((x) => ({ ...x, type_visite: r.data.types_visite[0] || x.type_visite })); }).catch(() => {}); }, []);
  const toggleDoc = (d) => setDocs((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.prenom || !f.nom) { toast.error("Prénom et nom requis"); return; }
    setLoading(true);
    try { await api.post("/novasol/vt", { ...f, documents_demandes: docs }); onSaved(); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="nova-vt-form">
        <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-bold">Demander une VT (NovaSol)</h2><button type="button" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Prénom"><input required data-testid="nova-prenom" className="nf" value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} /></FF>
          <FF label="Nom"><input required data-testid="nova-nom" className="nf" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} /></FF>
          <FF label="Téléphone"><input className="nf" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} /></FF>
          <FF label="Email"><input type="email" className="nf" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></FF>
          <FF label="Adresse"><input className="nf" value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} /></FF>
          <FF label="Code postal"><input data-testid="nova-cp" className="nf" value={f.code_postal} onChange={(e) => setF({ ...f, code_postal: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })} placeholder="Ex : 69008" /></FF>
          <FF label="Ville"><input className="nf" value={f.ville} onChange={(e) => setF({ ...f, ville: e.target.value })} /></FF>
          <FF label="Type de VT"><select data-testid="nova-type" className="nf" value={f.type_visite} onChange={(e) => setF({ ...f, type_visite: e.target.value })}>{types.map((t) => <option key={t} value={t}>{t}</option>)}</select></FF>
          <FF label="Date de visite (optionnel)"><DatePicker value={f.date_visite} onChange={(v) => setF({ ...f, date_visite: v })} testid="nova-date" /></FF>
        </div>
        <div className="mt-3"><FF label="Commentaire"><textarea className="nf" rows={2} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} /></FF></div>
        <div className="mt-3 rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Documents demandés</div>
          <div className="grid grid-cols-2 gap-2">
            {["CNI — Carte d'identité", "Avis d'imposition", "Taxe foncière", "Acte notarié", "Kbis SCI"].map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={docs.includes(d)} onChange={() => toggleDoc(d)} /> {d}</label>
            ))}
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
          <button data-testid="nova-vt-submit" disabled={loading} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer au planning CVT</button>
        </div>
        <style>{`.nf{width:100%;border:1px solid #e2e8f0;border-radius:.5rem;padding:.5rem .625rem;font-size:.875rem;outline:none}.nf:focus{border-color:#94a3b8}`}</style>
      </form>
    </div>
  );
}

function Header({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{title}</h1><p className="text-sm text-slate-500">{subtitle}</p></div>
      {children}
    </div>
  );
}
function FF({ label, children }) { return <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>{children}</div>; }
function Spin() { return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>; }
